import {computed, effect, inject, Injectable, InjectionToken, signal} from '@angular/core';
import {config} from '../../config';

export const ReferralStorageKey = 'entryPoint:referral';

/**
 * How the current document was loaded (`performance` navigation timing). Injected rather than read
 * inline so specs can choose between the restore and clear branches.
 */
export const DocumentNavigationType = new InjectionToken<NavigationTimingType | undefined>('DocumentNavigationType', {
  providedIn: 'root',
  factory: () => (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type,
});

export interface ReferralEntry {
  /** The GUID from `pks=MPRF-{guid}`, lower-cased. */
  readonly referralSourceId: string;
  /** `sbo`: the person at the keyboard is scheduling on behalf of the patient. */
  readonly schedulingOnBehalfOf: boolean;
  /** The main-site page the visit came from, when the referrer is the main site. */
  readonly returnUrl: string | null;
}

const ReferralSourceParam = 'pks';
const GuidPattern = '[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}';
const ReferralSourcePattern = new RegExp(`^MPRF-(${GuidPattern})$`, 'i');
const GuidOnlyPattern = new RegExp(`^${GuidPattern}$`, 'i');
const EmptyGuid = '00000000-0000-0000-0000-000000000000';

function firstValue(value: unknown): unknown {
  return Array.isArray(value) ? value[0] : value;
}

function validGuid(value: unknown): string | null {
  if (typeof value !== 'string' || !GuidOnlyPattern.test(value)) {
    return null;
  }
  const guid = value.toLowerCase();
  return guid === EmptyGuid ? null : guid;
}

/** `MPRF-{guid}` → the lower-cased GUID. Anything else, including the all-zero GUID, is not a referral source. */
export function parseReferralSourceId(value: unknown): string | null {
  value = firstValue(value);
  if (typeof value !== 'string') {
    return null;
  }
  return validGuid(ReferralSourcePattern.exec(value.trim())?.[1]);
}

/**
 * Reads a referral entry from bridging query params. `pks` is matched case-insensitively, as the
 * loader does; `sbo` counts only as `1`/`true`, and only alongside a valid referral source.
 */
export function parseReferralEntry(query: Readonly<Record<string, unknown>>): Omit<ReferralEntry, 'returnUrl'> | null {
  const sourceKey = Object.keys(query).find(key => key.toLowerCase() === ReferralSourceParam);
  const referralSourceId = sourceKey === undefined ? null : parseReferralSourceId(query[sourceKey]);
  if (!referralSourceId) {
    return null;
  }
  const sbo = firstValue(query[config.mainSiteBridging.paramNames.schedulingOnBehalfOf]);
  return {
    referralSourceId,
    schedulingOnBehalfOf: typeof sbo === 'string' && /^(1|true)$/i.test(sbo.trim()),
  };
}

/**
 * `referrer` when it is a page on the main site, else null. Hostnames are compared (ignoring a leading
 * `www.`) rather than prefixes, so `https://mindpath.com.example.net` does not pass for the main site.
 * Protocol and port must match too: the exit guard appends the insurance selection to this URL, so an
 * `http:` referrer or another service on the same host must not become the return target.
 */
export function mainSiteReturnUrl(referrer: string | null | undefined, mainSite: string | null | undefined): string | null {
  if (!referrer || !mainSite) {
    return null;
  }
  try {
    const from = new URL(referrer);
    const site = new URL(mainSite);
    const host = (url: URL) => url.hostname.toLowerCase().replace(/^www\./, '');
    const sameSite = from.protocol === site.protocol && from.port === site.port && host(from) === host(site);
    return sameSite ? from.toString() : null;
  } catch {
    return null;
  }
}

function readStoredEntry(): ReferralEntry | null {
  try {
    const stored = JSON.parse(sessionStorage.getItem(ReferralStorageKey) ?? 'null') as Partial<ReferralEntry> | null;
    const referralSourceId = validGuid(stored?.referralSourceId);
    if (!stored || !referralSourceId) {
      return null;
    }
    return {
      referralSourceId,
      schedulingOnBehalfOf: stored.schedulingOnBehalfOf === true,
      returnUrl: typeof stored.returnUrl === 'string' ? stored.returnUrl : null,
    };
  } catch {
    return null;
  }
}

/**
 * Referral visits: patients sent by a referral partner through a bridged link tagged
 * `pks=MPRF-{guid}`.
 *
 * The entry comes from the bridged URL, not from the loader's 24h `referring_source` cookie, which
 * would turn every visit that day into a referral. It lives in `sessionStorage` under an `entryPoint:*`
 * key, so `app.reset()` leaves it alone.
 */
@Injectable({
  providedIn: 'root'
})
export class ReferralSourceService {

  readonly #entry = signal<ReferralEntry | null>(null);

  readonly isReferral = computed(() => !!this.#entry());
  readonly referralSourceId = computed(() => this.#entry()?.referralSourceId ?? null);
  readonly schedulingOnBehalfOf = computed(() => this.#entry()?.schedulingOnBehalfOf ?? false);
  readonly returnUrl = computed(() => this.#entry()?.returnUrl ?? null);

  readonly #isReload: boolean;

  constructor() {
    const navigationType = inject(DocumentNavigationType);
    this.#isReload = navigationType === 'reload';

    // A reload or history step is the same visit, so it keeps the referral. A fresh navigation is a new
    // visit and starts clean; the bridged guard applies its entry, if it has one.
    const restore = navigationType === 'reload' || navigationType === 'back_forward';
    this.#set(restore ? readStoredEntry() : null);

    // An effect rather than a write in #set: its first run lands after AuthService's
    // `sessionStorage.clear()` in the same bootstrap, so a restored entry is written back.
    effect(() => {
      const entry = this.#entry();
      if (entry) {
        sessionStorage.setItem(ReferralStorageKey, JSON.stringify(entry));
      } else {
        sessionStorage.removeItem(ReferralStorageKey);
      }
    });
  }

  /**
   * Applies the bridging params of the document's first navigation. A valid referral source replaces
   * whatever was restored; anything else clears it. The exception is a reload: by then the loader may
   * have stripped `pks` from the address bar, so the restored entry is kept.
   *
   * @returns whether this entry is a referral.
   */
  applyBridgedEntry(query: Readonly<Record<string, unknown>>, referrer: string): boolean {
    const parsed = parseReferralEntry(query);
    if (parsed) {
      this.#set({...parsed, returnUrl: mainSiteReturnUrl(referrer, config.endpoints?.mainSite)});
      return true;
    }
    if (!this.#isReload) {
      this.#set(null);
    }
    return false;
  }

  #set(entry: ReferralEntry | null) {
    this.#entry.set(entry);
    // Synchronous, so anything rendered in the same pass already shows the right numbers.
    config.supportProfile.set(entry ? 'referral' : 'default');
  }
}
